import React, { useCallback } from 'react';
import { MockService, SV, TRANSACTIONAL } from '../../../../../types/rest/mockServiceTypes';
import BzmIcon from '../../../../../components/icons/BzmIcon';
import { hasAppliedTemplate, isBlazeVseMockService } from '../../../../../utils/mockServiceUtils';
import clsx from 'clsx';
import styles from '../MockServicesListBlazeTable.module.css';
import StopMockBtn from '../../../../../components/buttons/mockServiceButtons/StopMockBtn';
import StartMockBtn from '../../../../../components/buttons/mockServiceButtons/StartMockBtn';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { startRecordingThunk, stopRecordingThunk } from '../../../../../store/mockServices/mockServiceActions';
import DropdownButton from '../../../../../components/ui/dropdown/DropdownButton';
import {
  BlazeDropdownItem,
  buildDropdownButtonToggle,
} from '../../../../../components/ui/dropdown/DropdownButton/DropdownButton';

interface Props {
  mockService: MockService;
  isBlazeVseFeatureEnabled: boolean;
  stopMockService: (mockService: MockService) => void;
  createTemplateFromServiceMock: (mockService: MockService) => void;
  deleteMockService: () => void;
  openAssignMockServiceTemplateModal: () => void;
  deployMockService: () => void;
  isPrivateCloudReadOnly: boolean;
  openAnalytics: () => void;
  isCompact?: boolean /** If true, some icons will be presented as a dropdown menu */;
}

const MsActionButtons = ({
  mockService,
  isBlazeVseFeatureEnabled,
  stopMockService,
  createTemplateFromServiceMock,
  isPrivateCloudReadOnly,
  openAnalytics,
  deleteMockService,
  openAssignMockServiceTemplateModal,
  deployMockService,
  isCompact,
}: Props) => {
  const { t } = useTranslation('mockServices');
  const dispatch = useDispatch();
  const createTemplateBtn = (rowData: any) => (
    <button
      id={`mockServiceCreateTemplateButton` + rowData.id}
      className="btn btn-link-dark btn-no-hover"
      title={t('mockServicesTable.createMockServiceTemplate')}
      onClick={() => createTemplateFromServiceMock(rowData)}
      disabled={isPrivateCloudReadOnly || rowData.metaData.inProgress}
    >
      <BzmIcon variant={'icon-bzm-template-create'} className={'icon-xs'} />
    </button>
  );

  const msAnalyticsBtn = (mockService: MockService) => {
    return (
      <button
        disabled={isPrivateCloudReadOnly}
        id={`mockServiceAnalyticsButton` + mockService.id}
        className="btn btn-link-dark btn-no-hover"
        title={t('mockServicesTable.goMockServiceAnalytics')}
        onClick={openAnalytics}
      >
        <BzmIcon variant={'icon-bzm-chart-bar'} />
      </button>
    );
  };

  const msDeleteBtn = (mockService: MockService) => (
    <button
      id={`mockServiceDeleteServiceButton` + mockService.id}
      className="btn btn-link-dark btn-no-hover"
      disabled={isPrivateCloudReadOnly}
      onClick={deleteMockService}
      title={t('mockServicesTable.removeMockService')}
    >
      <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
    </button>
  );

  const assignTemplateBtn = (mockService: MockService) => {
    const isCreatedFromTemplate = hasAppliedTemplate(mockService);
    const classNames = clsx(isCreatedFromTemplate ? styles.alwaysVisible : undefined, 'btn btn-link-dark btn-no-hover');
    return (
      <button
        id={`mockServiceAssignTemplateButton` + mockService.id}
        className={classNames}
        title={
          !isCreatedFromTemplate
            ? t('mockServicesTable.assignMockServiceTemplate')
            : 'Virtual Service was created using a template.'
        }
        onClick={openAssignMockServiceTemplateModal}
        disabled={isPrivateCloudReadOnly || mockService.metaData.inProgress || mockService.metaData.isFailed}
      >
        <span
          className={clsx(isCreatedFromTemplate ? 'icon-bzm-template-applied' : 'icon-bzm-template', 'icon-xs')}
          aria-hidden="true"
        />
      </button>
    );
  };

  const stopMockServiceBtn = (mockService: MockService) => {
    return (
      <StopMockBtn id={mockService.id} disabled={isPrivateCloudReadOnly} onStop={() => stopMockService(mockService)} />
    );
  };

  const runMockServiceBtn = (mockService: MockService) => {
    //At least one runner has to be enabled to allow running the mock service
    const isOneRunnerEnabled = mockService.httpRunnerEnabled || mockService.messagingRunnerEnabled;
    const isDisabledWithoutRunner = !isOneRunnerEnabled && mockService.status !== 'RUNNING';
    return (
      <StartMockBtn
        id={mockService.id}
        disabled={
          isDisabledWithoutRunner ||
          mockService.metaData.isInvalid ||
          mockService.type === SV ||
          isPrivateCloudReadOnly ||
          mockService.metaData.inProgress
        }
        onClick={deployMockService}
      />
    );
  };

  function isMockServiceDeployed(rowData: MockService) {
    return rowData.isNew === false;
  }

  // SV analytics available only for deployed BlazeVSE MockServices when blazeVse feature flag is enabled
  const isBlazeVseAnalyticsAvailable =
    mockService.type === SV &&
    isBlazeVseFeatureEnabled &&
    isMockServiceDeployed(mockService) &&
    isBlazeVseMockService(mockService) &&
    (mockService.lastHeartBeat !== undefined || mockService.status === 'RUNNING');

  // Txn analytics available only for deployed virtual services
  const isTransactionalAnalyticsAvailable =
    mockService.type === TRANSACTIONAL &&
    isMockServiceDeployed(mockService) &&
    (mockService.lastHeartBeat !== undefined || mockService.status === 'RUNNING');

  const getEllipsisBtnWithDropdown = useCallback(
    (mockService: MockService) => {
      const createTemplateDropdown = (
        <span>
          <BzmIcon variant={'icon-bzm-template-create'} className={'mr-3'} />
          Create Template
        </span>
      );

      const applyTemplateDropdown = (
        <span
          title={
            !hasAppliedTemplate(mockService)
              ? t('mockServicesTable.assignMockServiceTemplate')
              : 'Virtual Service was created using a template.'
          }
          onClick={openAssignMockServiceTemplateModal}
        >
          <BzmIcon variant={'icon-bzm-template'} className={'mr-3'} />
          Apply Template
        </span>
      );
      const dropdownItems: BlazeDropdownItem[] = [
        {
          content: applyTemplateDropdown,
          title: 'Apply Template',
          fnCallback: openAssignMockServiceTemplateModal,
        },
        {
          content: createTemplateDropdown,
          title: 'Create Template',
          disabled: isPrivateCloudReadOnly,
          fnCallback: () => createTemplateFromServiceMock(mockService),
        },
      ];

      const dropdownToggle = <BzmIcon variant={'icon-bzm-ellipsis-vertical'} />;

      return (
        <DropdownButton
          customToggle={buildDropdownButtonToggle({
            title: 'Mock Service Menu',
            disabled: false,
            toggleComponent: dropdownToggle,
          })}
          dropdownItems={dropdownItems}
          toggleTooltip={t('mockServicesTable.createNew')}
          innerButtonSpanClassName="icon-bzm-add"
          inline={true}
          dropdownToggleClassName={`btn btn-primary btn-circular btn-jb `}
        />
      );
    },
    [mockService],
  );

  const getRecordingsDropdown = useCallback(
    (mockService: MockService) => {
      const recordingConfigs = [
        {
          type: 'MESSAGING',
          status: mockService.recorderStatus,
          label: 'Messaging',
        },
        {
          type: 'HTTP',
          status: mockService.httpRecorderStatus,
          label: 'Http',
        },
      ];

      const renderDropdownItem = ({ type, status, label }: { type: string; status: string; label: string }) => {
        const isRecording = status === 'RUNNING';
        return {
          content: (
            <button
              type="button"
              className={clsx('btn btn-link-dark btn-no-hover icon-xs', isRecording && 'text-danger')}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => {
                dispatch(isRecording ? stopRecordingThunk(mockService, type) : startRecordingThunk(mockService, type));
              }}
            >
              <BzmIcon variant={'icon-bzm-record'} className={'icon-xs'} />
              <span style={{ fontSize: '12px' }} className="icon-xs">
                {isRecording ? `Stop ${label} Recording` : `Start ${label} Recording`}
              </span>
            </button>
          ),
          title: isRecording ? `Stop ${label} Recording` : `Start ${label} Recording`,
          fnCallback: undefined, // handled by button onClick
        };
      };

      const dropdownItems: BlazeDropdownItem[] = recordingConfigs.map(renderDropdownItem);

      const isAnyRecording = mockService.recorderStatus === 'RUNNING' || mockService.httpRecorderStatus === 'RUNNING';

      const dropdownToggle = (
        <BzmIcon
          variant={'icon-bzm-record'}
          className={clsx('btn btn-link-dark btn-no-hover icon-xs mt-0', isAnyRecording && 'text-danger')}
        />
      );

      return (
        <DropdownButton
          customToggle={buildDropdownButtonToggle({
            title: 'Mock Service Menu',
            disabled: false,
            toggleComponent: dropdownToggle,
          })}
          dropdownItems={dropdownItems}
          toggleTooltip={t('mockServicesTable.createNew')}
          innerButtonSpanClassName="icon-bzm-add"
          inline={true}
          dropdownToggleClassName={`btn btn-primary btn-circular btn-jb `}
        />
      );
    },
    [dispatch, t],
  );

  // Helper to render the recording button
  const renderRecordingButton = (id: string, isRecording: boolean, title: string, onClick: () => void) => (
    <button
      id={id}
      style={{ marginTop: '2px' }}
      className={clsx('btn btn-link-dark btn-no-hover icon-xs', isRecording && 'text-danger')}
      title={title}
      onClick={onClick}
    >
      <BzmIcon variant={'icon-bzm-record'} className={'icon-xs'} />
    </button>
  );

  const recordingBtn = (mockService: MockService) => {
    if (
      mockService.status !== 'RUNNING' ||
      (!mockService.isRecorderModeActive && !mockService.isHttpRecorderModeActive)
    ) {
      return null;
    }

    if (mockService.isRecorderModeActive && mockService.isHttpRecorderModeActive) {
      return getRecordingsDropdown(mockService);
    }

    if (mockService.isRecorderModeActive) {
      const isRecording = mockService.recorderStatus === 'RUNNING';
      return renderRecordingButton(
        `mockServiceRecordingButton${mockService.id}`,
        isRecording,
        isRecording ? 'Stop Messaging Recording' : 'Start Messaging Recording',
        () => {
          dispatch(
            isRecording ? stopRecordingThunk(mockService, 'MESSAGING') : startRecordingThunk(mockService, 'MESSAGING'),
          );
        },
      );
    }

    if (mockService.isHttpRecorderModeActive) {
      const isRecording = mockService.httpRecorderStatus === 'RUNNING';
      return renderRecordingButton(
        `mockServiceRecordingButton${mockService.id}`,
        isRecording,
        isRecording ? 'Stop Http Recording' : 'Start Http Recording',
        () => {
          dispatch(isRecording ? stopRecordingThunk(mockService, 'HTTP') : startRecordingThunk(mockService, 'HTTP'));
        },
      );
    }

    return null;
  };

  const getCompactActionButtons = (mockService: MockService) => {
    return (
      <>
        {mockService.type === TRANSACTIONAL && <>{recordingBtn(mockService)}</>}
        {(isBlazeVseAnalyticsAvailable || isTransactionalAnalyticsAvailable) && msAnalyticsBtn(mockService)}
        {msDeleteBtn(mockService)}
        {getEllipsisBtnWithDropdown(mockService)}
      </>
    );
  };

  /**
   * Get action buttons - standard non-compact version.
   * @param mockService
   */
  const getActionButtons = (mockService: MockService) => {
    return (
      <>
        {mockService.type === TRANSACTIONAL && (
          <>
            {assignTemplateBtn(mockService)}
            {createTemplateBtn(mockService)}
            {recordingBtn(mockService)}
          </>
        )}
        {(isBlazeVseAnalyticsAvailable || isTransactionalAnalyticsAvailable) && msAnalyticsBtn(mockService)}
        {msDeleteBtn(mockService)}
      </>
    );
  };

  return (
    <>
      {/* Play & Stop buttons */}
      <div className="d-inline-block mr-2">
        {!mockService.metaData.isDeployed && !mockService.metaData.inProgress && runMockServiceBtn(mockService)}
        {mockService.metaData.isDeployed && !mockService.metaData.inProgress && stopMockServiceBtn(mockService)}
      </div>
      {/* ./ Play & Stop buttons */}

      {/* Action Buttons */}
      <div className={clsx('ml-2', 'flex-table-actions', 'd-inline-block')}>
        {!isCompact ? getActionButtons(mockService) : getCompactActionButtons(mockService)}
      </div>

      {/* ./ Action Buttons */}
    </>
  );
};

export default MsActionButtons;
